import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import type { Incident, IncidentStatus } from '@models/incidents';
import { UserPermissions } from '@models/user';
import type { GetIncidentsParams } from '@features/incidents/api';
import { INCIDENT_STATUS_OPTIONS, getIncidentUrl } from '@features/incidents/constants';
import { useIncidentsQuery } from '@features/incidents/queries';
import { useMeStore } from '@features/users/store';
import { useDebounce } from '@hooks/useDebounce';
import { useToast } from '@hooks/useToast';
import { userHasPermission } from '@utils/access';

import FilterDropdown from '@components/FilterDropdown';
import Pagination from '@components/Pagination';
import PrimaryButton from '@components/PrimaryButton';
import IncidentsTable from '@components/Incidents/IncidentsTable';
import IncidentFormDialog from '@components/Incidents/IncidentFormDialog';
import s from './IncidentsPage.module.css';

const DEFAULT_ROWS_PER_PAGE = 25;

type StatusFilter = IncidentStatus | 'all';

function IncidentsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMeStore();
  const canEdit = !!me && userHasPermission(me, UserPermissions.EDIT_USERS);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [createOpen, setCreateOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 300).trim();

  const params: GetIncidentsParams = {
    page: page + 1,
    limit: rowsPerPage,
    statuses: status === 'all' ? undefined : [status],
    search: debouncedSearch.length > 0 ? debouncedSearch : undefined,
  };

  const { data, isLoading, isFetching, isError } = useIncidentsQuery(params);

  const errorShownRef = useRef(false);
  useEffect(() => {
    if (isError && !errorShownRef.current) {
      toast.error('Failed to load incidents');
      errorShownRef.current = true;
    }
    if (!isError) errorShownRef.current = false;
  }, [isError, toast]);

  const updateSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const updateStatus = (value: StatusFilter) => {
    setStatus(value);
    setPage(0);
  };

  const hasFilters = search.length > 0 || status !== 'all';

  const openIncident = (incident: Incident) => navigate(getIncidentUrl(incident.id));

  return (
    <div className={s.container}>
      <header className={s.header}>
        <div>
          <h1 className={s.title}>Incidents</h1>
          <p className={s.subtitle}>Which customers each incident, backfill or data fix touched.</p>
        </div>
        {canEdit && (
          <PrimaryButton
            title="New incident"
            icon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          />
        )}
      </header>

      <div className={s.toolbar}>
        <div className={s.toolbarLeft}>
          <div className={s.search}>
            <SearchIcon className={s.searchIcon} sx={{ fontSize: 18 }} />
            <input
              type="text"
              className={s.searchInput}
              placeholder="Search incidents by name…"
              value={search}
              onChange={(event) => updateSearch(event.target.value)}
            />
            {search && (
              <button
                type="button"
                className={s.searchClear}
                onClick={() => updateSearch('')}
                aria-label="Clear search">
                <CloseIcon sx={{ fontSize: 12 }} />
              </button>
            )}
          </div>
          <FilterDropdown
            label="Status"
            value={status}
            options={INCIDENT_STATUS_OPTIONS}
            onChange={updateStatus}
          />
          {hasFilters && (
            <button
              type="button"
              className={s.clearBtn}
              onClick={() => {
                updateSearch('');
                updateStatus('all');
              }}>
              Clear
            </button>
          )}
        </div>
      </div>

      <IncidentsTable
        isLoading={isLoading}
        isFetching={isFetching}
        rows={data?.data ?? []}
        onRowClick={openIncident}
      />

      {!isLoading && (
        <Pagination
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          total={data?.meta.itemCount ?? 0}
        />
      )}

      {canEdit && (
        <IncidentFormDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSaved={openIncident}
        />
      )}
    </div>
  );
}

export default IncidentsPage;
